import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import * as api from "../api/client";

const MARK = { anthropic: ["Cl", "#D97757"], openai: ["AI", "#10A37F"], nvidia: ["NV", "#76B900"], custom: ["API", "#475569"] };
const ago = (s) => (s ? new Date(s * 1000).toLocaleString() : "—");

function Mark({ kind, size = 36 }) {
  const [t, c] = MARK[kind] || MARK.custom;
  return <span className="cx-engine" style={{ width: size, height: size, background: c, fontSize: size * 0.34 }}>{t}</span>;
}

export default function AiModelsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await api.listLlmProviders());
      window.dispatchEvent(new Event("llm-models-changed"));
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="cx-page">
      <header className="cx-head">
        <div>
          <h1 className="cx-title">AI models</h1>
          <p className="cx-sub">Connect Claude, ChatGPT, NVIDIA or any OpenAI-compatible model with an API key. Enabled models appear in everyone's model picker. Keys are encrypted on the server and never sent to browsers.</p>
        </div>
        <button type="button" className="cx-btn primary" onClick={() => setForm({ kind: "" })}>+ Connect provider</button>
      </header>
      {err && <div className="cx-msgbox bad">{err}</div>}
      <div className="cx-beta ai-note">
        Your self-hosted model keeps everything on your own servers. With an external provider, each question and the data needed to answer it are sent to that provider under its terms. Choose per use case.
      </div>
      {!data ? <div className="cx-card cx-loading"><Spinner animation="border" size="sm" /></div> : (
        <div className="ai-list">
          {data.providers.length === 0 && (
            <div className="cx-card cx-empty">
              <strong>No providers connected</strong>
              <span>QLens is using your self-hosted model. Connect a provider to offer Claude or ChatGPT as well.</span>
            </div>
          )}
          {data.providers.map((p) => <Provider key={p.id} p={p} onChange={load} onError={setErr} />)}
        </div>
      )}
      {form && data && <ConnectModal kinds={data.kinds} onClose={() => setForm(null)} onDone={() => { setForm(null); load(); }} />}
    </div>
  );
}

function Provider({ p, onChange, onError }) {
  const [enabled, setEnabled] = useState(() => p.models.filter((m) => m.enabled).map((m) => m.id));
  const [testing, setTesting] = useState("");
  const [filter, setFilter] = useState("");
  const dirty = JSON.stringify([...enabled].sort()) !== JSON.stringify(p.models.filter((m) => m.enabled).map((m) => m.id).sort());
  const shown = p.models.filter((m) => !filter || m.id.toLowerCase().includes(filter.toLowerCase()) || enabled.includes(m.id));

  async function test(model) {
    setTesting(model);
    try {
      await api.testLlmModel(p.id, model);
      await onChange();
    } catch (e) {
      onError(e.message);
    } finally {
      setTesting("");
    }
  }

  async function save() {
    try {
      await api.setLlmModels(p.id, enabled);
      await onChange();
    } catch (e) {
      onError(e.message);
    }
  }

  async function remove() {
    if (!window.confirm(`Disconnect ${p.label}? Its models disappear from the picker.`)) return;
    try {
      await api.deleteLlmProvider(p.id);
      await onChange();
    } catch (e) {
      onError(e.message);
    }
  }

  return (
    <section className="cx-card ai-prov">
      <div className="ai-prov-h">
        <Mark kind={p.kind} />
        <div>
          <strong>{p.label}</strong>
          <div className="cx-muted">{p.baseUrl}{p.keyHint ? ` · key ••••${p.keyHint}` : ""} · connected by {p.createdBy || "—"} · {ago(p.updatedAt)}</div>
        </div>
        <button type="button" className="cx-btn danger" onClick={remove}>Disconnect</button>
      </div>
      <div className="ai-models-h">
        <span><strong>{enabled.length}</strong> of {p.models.length} models offered</span>
        {p.models.length > 8 && <input className="ai-filter" placeholder="Filter models…" value={filter} onChange={(e) => setFilter(e.target.value)} />}
      </div>
      <div className="cx-scroll ai-models">
        <table className="cx-table">
          <thead><tr><th>Offer</th><th>Model</th><th>Check</th><th /></tr></thead>
          <tbody>
            {shown.map((m) => (
              <tr key={m.id}>
                <td><input type="checkbox" checked={enabled.includes(m.id)}
                           onChange={() => setEnabled((xs) => (xs.includes(m.id) ? xs.filter((x) => x !== m.id) : [...xs, m.id]))} /></td>
                <td><span className="cx-strong">{m.label}</span>{m.label !== m.id && <div className="cx-muted">{m.id}</div>}</td>
                <td>
                  {m.test ? (m.test.ok
                    ? <span className="cx-status ok">Works with QLens · {m.test.seconds}s</span>
                    : <><span className="cx-status failed">Failed</span><div className="cx-err">{m.test.error}</div></>)
                    : <span className="cx-muted">Not checked</span>}
                </td>
                <td className="cx-actions">
                  <button type="button" className="cx-link" disabled={!!testing} onClick={() => test(m.id)}>
                    {testing === m.id ? <Spinner size="sm" animation="border" /> : "Check"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ai-prov-f">
        <span className="cx-muted">“Check” sends one short request and confirms the model can call tools, which QLens agents need.</span>
        <button type="button" className="cx-btn primary" disabled={!dirty} onClick={save}>Save</button>
      </div>
    </section>
  );
}

function ConnectModal({ kinds, onClose, onDone }) {
  const [kind, setKind] = useState("");
  const [f, setF] = useState({ apiKey: "", baseUrl: "", label: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const k = kinds.find((x) => x.id === kind);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.connectLlmProvider({ kind, ...f });
      onDone();
    } catch (er) {
      setErr(er.message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="cx-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Connect provider">
        <div className="cx-modal-h">
          <div>
            <div className="cx-modal-title">Connect an AI provider</div>
            <div className="cx-muted">{k ? k.label : "Choose a provider"}</div>
          </div>
          <button type="button" className="cx-x-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        {!k ? (
          <div className="cx-modal-body cx-engines">
            {kinds.map((x) => (
              <button key={x.id} type="button" className="cx-engine-card" onClick={() => setKind(x.id)}>
                <Mark kind={x.id} />
                <span className="cx-engine-txt"><span className="cx-engine-name">{x.label}</span><span className="cx-muted">{x.vendor}</span></span>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={submit} className="cx-form-wrap">
            <div className="cx-modal-body">
              <div className="cx-chosen">
                <Mark kind={k.id} size={32} />
                <div><strong>{k.label}</strong><div className="cx-muted">{k.hint}</div></div>
                <button type="button" className="cx-link" onClick={() => setKind("")}>Change</button>
              </div>
              <div className="cx-grid">
                {k.id === "custom" && (
                  <>
                    <label className="cx-field span2">
                      <span>Base URL</span>
                      <input value={f.baseUrl} onChange={(e) => setF({ ...f, baseUrl: e.target.value })} placeholder="https://host/v1" required />
                    </label>
                    <label className="cx-field span2">
                      <span>Name<em> · optional</em></span>
                      <input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} maxLength={60} placeholder="e.g. Azure OpenAI (India)" />
                    </label>
                  </>
                )}
                <label className="cx-field span2">
                  <span>API key{k.id === "custom" && <em> · if the endpoint needs one</em>}</span>
                  <input type="password" value={f.apiKey} onChange={(e) => setF({ ...f, apiKey: e.target.value })}
                         autoComplete="off" spellCheck="false" required={k.id !== "custom"} />
                </label>
              </div>
              <p className="cx-muted cx-note">QLens checks the key by listing the models it can use. The key is encrypted at rest and only the last 4 characters are ever shown.</p>
              {err && <div className="cx-msgbox bad">{err}</div>}
            </div>
            <div className="cx-modal-f">
              <button type="submit" className="cx-btn primary" disabled={busy}>{busy ? <Spinner size="sm" animation="border" /> : "Connect"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
